/*
  IA PDP Subscription — <ia-pdp-subscription>. Deliver one-time / subscribe
  card (ia-pdp-subscription.css, blocks/_ia-pdp-subscription.liquid).

  Event contract (unchanged from the legacy widget — ia-product-buy.js and
  ia-pdp.js consume it):
  - publishes EVENTS.cadenceChange / EVENTS.planChange with
    { sectionId, productId, plan: {id}, cadence, variant_price, plan_price }
  - the root's `cadence` attribute mirrors the selection;
    [data-selling-plan-select] holds the active plan (options carry
    data-plan-price, money-formatted server-side).

  The block re-renders on variant change (data-re-render); ia-pdp.js calls
  restore() on the fresh element to re-apply the shopper's choice.
*/
import { EVENTS, publish } from 'ia-util-pubsub'

if (!customElements.get('ia-pdp-subscription')) {
  class IaPdpSubscription extends HTMLElement {
    connectedCallback() {
      this.planSelect = this.querySelector('[data-selling-plan-select]')
      this.options = Array.from(this.querySelectorAll('[data-cadence]'))

      const widgetData = this.querySelector('[data-widget-data]')
      this.data = widgetData ? JSON.parse(widgetData.textContent) : {}

      this.options.forEach((option) => {
        option.addEventListener('click', this.handleOptionClick.bind(this))
      })
      this.querySelectorAll('[data-cadence-select]').forEach((radio) => {
        radio.addEventListener('change', this.handleRadioChange.bind(this))
      })
      this.planSelect?.addEventListener('change', () => this.publishEvent(EVENTS.planChange))

      // Sync consumers (buy-button price) with the server-rendered default.
      this.publishEvent(EVENTS.cadenceChange)
    }

    get sectionId() {
      return this.getAttribute('section-id')
    }

    get productId() {
      return this.getAttribute('product-id')
    }

    get cadence() {
      return this.getAttribute('cadence')
    }

    set cadence(value) {
      this.setAttribute('cadence', value)
    }

    handleOptionClick(event) {
      if (event.target.closest('select, .ia-pdp-sub__select')) return
      const cadence = event.currentTarget.dataset.cadence
      if (cadence && cadence !== this.cadence) {
        this.setCadence(cadence)
        this.publishEvent(EVENTS.cadenceChange)
      }
    }

    handleRadioChange(event) {
      this.setCadence(event.target.value)
      this.publishEvent(EVENTS.cadenceChange)
    }

    setCadence(cadence) {
      this.cadence = cadence
      this.options.forEach((option) => {
        const isActive = option.dataset.cadence === cadence
        option.classList.toggle('is-active', isActive)
        const radio = option.querySelector('[data-cadence-select]')
        if (radio) radio.checked = isActive
      })
    }

    /* Re-apply a previous selection after a data-re-render swap (ia-pdp.js). */
    restore(cadence, planId) {
      if (planId && this.planSelect) {
        const match = Array.from(this.planSelect.options).find((option) => option.value === String(planId))
        if (match) this.planSelect.value = match.value
      }
      if (cadence) this.setCadence(cadence)
      this.publishEvent(EVENTS.cadenceChange)
    }

    publishEvent(eventName) {
      const planId = this.planSelect?.value
      const selected = this.planSelect?.options[this.planSelect.selectedIndex]?.dataset
      const planPrice = selected?.planPrice

      publish(eventName, {
        detail: {
          sectionId: this.sectionId,
          productId: this.productId,
          plan: planId ? { id: planId } : null,
          cadence: this.cadence,
          variant_price: this.data.variant_price,
          plan_price: planPrice || this.data.plan_price,
          // Cents alongside the formatted price, for consumers that calculate (ia-pdp-loyalty).
          plan_price_cents: selected?.planPriceCents
        }
      })
    }
  }

  customElements.define('ia-pdp-subscription', IaPdpSubscription)
}
